<template>
  <div class="depliant-view flex h-[calc(100vh-80px)]">
    <!-- Détecteur de changement de marque -->
    <BrandChangeDetector :support-id="route.params.id" />

    <!-- Overlay de chargement -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
      </div>
    </div>

    <!-- Panneau latéral -->
    <ProductsPanel />

    <!-- Zone principale -->
    <div class="flex-1 bg-gray-100 p-8 overflow-auto">
      <div class="max-w-5xl mx-auto">
        <!-- En-tête -->
        <div class="mb-8 flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {{
              route.params.id
                ? "Édition d'un dépliant"
                : "Création d'un dépliant"
            }}
          </h1>
          <div class="flex items-center gap-4">
            <button
              v-if="currentPage === 1"
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="isSaving"
              @click="saveSupport()"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin" />
              <span>{{ isSaving ? "Enregistrement..." : "Enregistrer" }}</span>
            </button>
            <div v-else class="text-sm text-gray-500 italic">
              Retournez à la page 1 pour enregistrer ou générer le PDF
            </div>
          </div>
        </div>

        <!-- Configuration -->
        <div v-if="!supportStore.isPreview" class="mb-8 grid grid-cols-2 gap-4">
          <div class="flex items-end gap-2">
            <button
              v-for="page in 4"
              :key="page"
              :ref="page === currentPage ? 'currentPageButton' : undefined"
              class="px-4 py-2 rounded-md shadow-sm transition-colors page-button"
              :class="[
                currentPage === page
                  ? 'bg-brand text-white active'
                  : 'bg-white border border-gray-300 hover:bg-gray-50',
              ]"
              @click="togglePage(page)"
            >
              Page {{ page }}
            </button>
          </div>
        </div>

        <!-- Canvas -->
        <SupportCanvas
          ref="supportCanvasRef"
          :support="supportStore.currentSupport"
          :show-header="currentPage === 1"
          :selected-pages="selectedPages"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProductsPanel from "@/components/supports/panels/ProductsPanel.vue";
import SupportCanvas from "@/components/supports/SupportCanvas.vue";
import BrandChangeDetector from "@/components/supports/BrandChangeDetector.vue";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import { useHtmlDownload } from "@/composables/supports/useHtmlDownload";
import { onMounted, ref, watch, nextTick } from "@vue/runtime-core";
import type { Support, SupportPage, SupportElement } from "@/types/support";
import { useToast } from "vue-toastification";
import { useRoute, useRouter } from "vue-router";

const supportStore = useSupportStore();
const currentPage = ref(1);
const SUPPORT_TEMPLATE_ID = 3; // ID du template dépliant

// Variables pour la sélection des pages
const selectedPages = ref<string>("all");

const toast = useToast();
const titleText = ref("Votre titre");
const validityPeriod = ref("du XX au XX mois ANNÉE");

const route = useRoute();
const router = useRouter();
const supportCanvasRef = ref();
const { checkForHtmlDownload } = useHtmlDownload(supportCanvasRef);
const currentPageButton = ref<HTMLElement>();

const isSaving = ref(false);
const successSound = new Audio("/sounds/success.wav");
const errorSound = new Audio("/sounds/error.mp3");

const togglePage = (page: number): void => {
  currentPage.value = page;
  if (supportStore.currentSupport) {
    supportStore.updateSupport({
      currentPage: page,
      type: "depliant",
    });

    // S'assurer que la page a des éléments initialisés
    const targetPage = supportStore.currentSupport.pages.find(
      (p: SupportPage) => p.page_number === page
    );

    if (
      targetPage &&
      (!Array.isArray(targetPage.elements) || targetPage.elements.length === 0)
    ) {
      targetPage.elements = [null];
      supportStore.updateSupport({
        pages: supportStore.currentSupport.pages,
      });
    }

    // Forcer la réactivité avec nextTick
    nextTick(() => {
      // Vérifier que la page est bien mise à jour
      if (supportStore.currentSupport) {
        const updatedPage = supportStore.currentSupport.pages.find(
          (p: SupportPage) => p.page_number === page
        );
        if (
          updatedPage &&
          (!Array.isArray(updatedPage.elements) ||
            updatedPage.elements.length === 0)
        ) {
          updatedPage.elements = [null];
          supportStore.updateSupport({
            pages: supportStore.currentSupport.pages,
          });
        }
      }

      // Mettre le focus sur le bouton de la page courante
      focusCurrentPageButton();
    });
  }
};

const playSound = (sound: HTMLAudioElement) => {
  sound.currentTime = 0; // Réinitialiser le son
  sound.play().catch((error) => {
    console.warn("Erreur lors de la lecture du son:", error);
  });

  // Couper le son après 200ms pour le son iPhone
  setTimeout(() => {
    sound.pause();
    sound.currentTime = 0;
  }, 400);
};

// Ajuster le volume des sons
successSound.volume = 0.3; // 30% du volume pour le son iPhone
errorSound.volume = 0.15; // 15% du volume pour l'erreur

const generateAndGetPdfBlob = async (): Promise<Blob> => {
  // Sauvegarder la page courante
  const originalPage = currentPage.value;

  // Synchronisation du store avec les données du composant
  if (
    supportCanvasRef.value &&
    typeof supportCanvasRef.value.getCurrentState === "function"
  ) {
    const currentState = supportCanvasRef.value.getCurrentState();
    if (currentState) {
      // Met à jour le store avec l'état courant du composant (pages, produits, pictos, etc.)
      supportStore.updateSupport(currentState);
    }
  }

  // Forcer la page 1 pendant la génération
  currentPage.value = 1;
  if (supportStore.currentSupport) {
    supportStore.updateSupport({
      currentPage: 1,
    });
  }

  // Attendre que le composant soit mis à jour
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Forcer un rafraîchissement du composant SupportCanvas
  if (supportCanvasRef.value) {
    // Désactiver temporairement le composant
    supportCanvasRef.value.$el.style.display = "none";
    await new Promise((resolve) => setTimeout(resolve, 50));
    // Réactiver le composant pour forcer un rafraîchissement
    supportCanvasRef.value.$el.style.display = "block";
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // Activer le mode preview si nécessaire
  let previewWasActivated = false;
  if (!supportStore.isPreview) {
    supportStore.togglePreview();
    previewWasActivated = true;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  // Générer le PDF avec toutes les pages
  const pdfBlob = await supportCanvasRef.value.generatePdfBlob();

  // Restaurer l'état du preview si on l'a activé
  if (previewWasActivated) {
    supportStore.togglePreview();
  }

  // Restaurer la page originale
  currentPage.value = originalPage;
  if (supportStore.currentSupport) {
    supportStore.updateSupport({
      currentPage: originalPage,
    });
  }

  // Attendre que le composant soit mis à jour avec la page originale
  await new Promise((resolve) => setTimeout(resolve, 100));

  return pdfBlob;
};

const saveSupport = async (): Promise<void> => {
  try {
    isSaving.value = true;

    // Sauvegarder la page courante
    const originalPage = currentPage.value;

    // 1. Sauvegarder le support d'abord (le PDF est généré côté serveur).
    const supportData = {
      ...supportStore.currentSupport,
      name: titleText.value,
      dates: validityPeriod.value,
      repiquage: supportStore.currentSupport?.repiquage || null,
      pages: supportStore.currentSupport?.pages.map((page: SupportPage) => ({
        ...page,
        elements: page.elements.filter(
          (el: SupportElement | null): el is SupportElement => el !== null
        ),
      })),
    };

    const response = await supportStore.saveSupport(supportData);
    if (response?.data?.id) {
      supportStore.currentSupport.id = response.data.id;
    }

    // 2. FormData (sans PDF : le serveur génère le vectoriel ; capture en fallback)
    const formData = new FormData();
    formData.append("supportId", supportStore.currentSupport.id.toString());
    formData.append("brandSlug", localStorage.getItem("brand") || "hv");
    formData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );

    // 3. Persistance du PDF (vectoriel async + notification ; capture en fallback)
    await supportStore.persistSupportPdf(
      formData,
      () => generateAndGetPdfBlob(),
      // HTML EXACT de l'éditeur -> rendu vectoriel fidèle au pixel côté serveur.
      () => supportCanvasRef.value.generateFullHTML()
    );
    playSound(successSound);
    toast.success("Support sauvegardé avec succès");
    router.push("/documents");
  } catch (error) {
    console.error("Erreur lors de la sauvegarde:", error);
    playSound(errorSound);
    toast.error("Erreur lors de la sauvegarde du support");
  } finally {
    isSaving.value = false;
  }
};

// Fonction pour mettre le focus sur le bouton de la page courante
const focusCurrentPageButton = () => {
  nextTick(() => {
    if (currentPageButton.value) {
      currentPageButton.value.focus();
    }
  });
};

onMounted(async (): Promise<void> => {
  const supportId = route.params.id;
  if (supportId) {
    // Mode édition
    try {
      await supportStore.loadSupport("depliant", supportId as string);
      if (supportStore.currentSupport) {
        titleText.value = supportStore.currentSupport.name;
        validityPeriod.value = supportStore.currentSupport.dates;

        // S'assurer que les 4 pages sont correctement initialisées
        if (supportStore.currentSupport.pages.length < 4) {
          const existingPages = [...supportStore.currentSupport.pages];
          const newPages: SupportPage[] = [];

          // Créer les pages manquantes
          for (let i = 1; i <= 4; i++) {
            const existingPage = existingPages.find((p) => p.page_number === i);
            if (existingPage) {
              // S'assurer que la page a des éléments
              if (
                !Array.isArray(existingPage.elements) ||
                existingPage.elements.length === 0
              ) {
                existingPage.elements = [null];
              }
              newPages.push(existingPage);
            } else {
              newPages.push({
                id: i,
                page_number: i,
                support_id: supportStore.currentSupport.id,
                nb_max_products: 5,
                page_settings: {
                  margin_top: 10,
                  margin_bottom: 10,
                  margin_left: 10,
                  margin_right: 10,
                  bleed: 5,
                  safe_area: true,
                },
                background_image: null,
                page_zoom: "1.00",
                page_rotation: 0,
                page_position: {
                  x: 0,
                  y: 0,
                  width: 595,
                  height: 842,
                },
                elements: [null], // Initialiser avec un élément null
              });
            }
          }

          // Mettre à jour le store avec les 4 pages
          supportStore.updateSupport({
            support_template_id: SUPPORT_TEMPLATE_ID,
            pages: newPages,
            nb_pages: 4,
            currentPage: 1, // Forcer la page 1
          });
        } else {
          // S'assurer que toutes les pages existantes ont des éléments
          const updatedPages = supportStore.currentSupport.pages.map(
            (page: SupportPage) => ({
              ...page,
              elements:
                Array.isArray(page.elements) && page.elements.length > 0
                  ? page.elements
                  : [null],
            })
          );

          supportStore.updateSupport({
            pages: updatedPages,
            currentPage: 1, // Forcer la page 1
          });
        }

        // S'assurer que la page courante est bien définie à 1
        currentPage.value = 1;
        supportStore.updateSupport({
          currentPage: 1,
          type: "depliant",
        });

        // Restaurer le repiquage si présent
        if (supportStore.currentSupport.repiquage) {
          // S'assurer que le repiquage est bien restauré dans le store
          supportStore.updateSupport({
            repiquage: supportStore.currentSupport.repiquage,
          });
        }

        // Mettre le focus sur le bouton de la page courante
        focusCurrentPageButton();
      }
    } catch (error) {
      console.error("Erreur lors du chargement du support:", error);
      toast.error("Erreur lors du chargement du support");
    }
  } else {
    // Mode création
    supportStore.initSupport("depliant");

    // Forcer immédiatement la page 1
    currentPage.value = 1;

    // S'assurer que les pages sont initialisées avec des éléments
    nextTick(() => {
      if (supportStore.currentSupport) {
        const updatedPages = supportStore.currentSupport.pages.map(
          (page: SupportPage) => ({
            ...page,
            elements:
              Array.isArray(page.elements) && page.elements.length > 0
                ? page.elements
                : [null],
          })
        );

        supportStore.updateSupport({
          pages: updatedPages,
          currentPage: 1,
          type: "depliant",
        });

        // Mettre le focus sur le bouton de la page courante
        focusCurrentPageButton();
      }
    });
  }
  
  // Vérifier si on doit télécharger le HTML automatiquement
  checkForHtmlDownload();
});

watch(
  currentPage,
  () => {
    if (supportStore.currentSupport) {
      // S'assurer que la page courante a des éléments
      const targetPage = supportStore.currentSupport.pages.find(
        (page: SupportPage) => page.page_number === currentPage.value
      );

      if (
        targetPage &&
        (!Array.isArray(targetPage.elements) ||
          targetPage.elements.length === 0)
      ) {
        targetPage.elements = [null];
      }

      supportStore.updateSupport({
        currentPage: currentPage.value,
        support_template_id: SUPPORT_TEMPLATE_ID,
        pages: supportStore.currentSupport.pages.map((page: SupportPage) => ({
          ...page,
          elements: page.elements
            .filter(
              (el: SupportElement | null): el is SupportElement => el !== null
            )
            .map((element: SupportElement) => ({
              ...element,
              product_image_id: element.product_image_id,
            })),
        })),
      });

      // Forcer la réactivité avec nextTick
      nextTick(() => {
        // Vérifier que la page est bien mise à jour
        if (supportStore.currentSupport) {
          const updatedPage = supportStore.currentSupport.pages.find(
            (page: SupportPage) => page.page_number === currentPage.value
          );
          if (
            updatedPage &&
            (!Array.isArray(updatedPage.elements) ||
              updatedPage.elements.length === 0)
          ) {
            updatedPage.elements = [null];
            supportStore.updateSupport({
              pages: supportStore.currentSupport.pages,
            });
          }
        }

        // Mettre le focus sur le bouton de la page courante
        focusCurrentPageButton();
      });
    }
  },
  { immediate: true }
);
</script>

<script lang="ts">
export default {
  name: "DepliantView",
};
</script>

<style scoped>
/* Styles pour l'impression et l'aperçu */
@media print {
  .support-preview {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* Styles pour les boutons de page */
.page-button:focus {
  outline: 2px solid #b70018;
  outline-offset: 2px;
  box-shadow: 0 0 0 3px rgba(183, 0, 24, 0.1);
}

.page-button:focus-visible {
  outline: 2px solid #b70018;
  outline-offset: 2px;
}

/* Indicateur visuel pour la page active */
.page-button.active {
  position: relative;
  animation: pulse-active 2s infinite;
}

.page-button.active::after {
  content: "";
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  background-color: #b70018;
  border-radius: 50%;
}

@keyframes pulse-active {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(183, 0, 24, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(183, 0, 24, 0);
  }
}
</style>
